import { Component, DestroyRef, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { first, map } from "rxjs/operators";
import { LOREN_ROUTES } from "../../../../shared/constants/loren-routes.constants";
import { LorenDetailsOutletService } from "../../../../shared/services/loren-details-outlet-service/loren-details-outlet.service";
import { LorenNotification } from "../../../../shared/services/loren-notification-service/interfaces/loren-notification.interface";
import { LorenNotificationService } from "../../../../shared/services/loren-notification-service/loren-notification.service";
import { LorenMessagingService } from "../../../../shared/services/loren-messaging-service/loren-messaging.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LOREN_DATE } from "../../../../shared/constants/loren-date.constants";
import { LorenDateUtilsService } from "../../../../shared/services/loren-date-utils-service/loren-date-utils.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "loren-notifications-panel",
  templateUrl: "./loren-notifications-panel.component.html",
  styleUrls: ["./loren-notifications-panel.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenNotificationsPanelComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private snackbarService = inject(LorenSnackBarService);
  private notificationService = inject(LorenNotificationService);
  private detailsOutletService = inject(LorenDetailsOutletService);
  private messagingService = inject(LorenMessagingService);

  readonly pushPermission$ = this.messagingService.permission$;

  private readonly destroyRef = inject(DestroyRef);
  allNotifications: LorenNotification[] = [];
  notificationItemSize = 72;
  scrollHeight: number;
  searchString: string;
  filteredNotifications: LorenNotification[] = [];
  detailsOutletState$ = this.detailsOutletService.getCurrentState();
  hasNewNotifications = false;

  // private retentionPeriodReadNotifications: number;
  // private retentionPeriodUnreadNotifications: number;
  private refreshIntervalDuration = 30 * 1000;
  private refreshInterval;

  ngOnInit() {
    // this.getNotificationRetentionPeriods(companyId, id);
    this.getNotifications();
    this.refreshInterval = window.setInterval(() => {
      this.getNotifications();
    }, this.refreshIntervalDuration);
  }

  ngOnDestroy() {
    clearInterval(this.refreshInterval);
  }

  handleBackClick() {
    this.closeDetails();
  }

  handleNotificationClick($event: LorenNotification) {
    const notificationIdx = this.filteredNotifications.findIndex(
      (x) => x.id === $event.id
    );
    if (notificationIdx > -1) {
      this.filteredNotifications[notificationIdx].seen = true;
    }

    // Update the flag if this was the last unseen notification
    this.hasNewNotifications = this.allNotifications.some(notification => !notification.seen);

    this.closeDetails();
    setTimeout(() => this.router.navigate([$event.path]), 100);
  }

  handleSearch() {
    /// getFilteredNotifications()
    this.filteredNotifications = this.allNotifications.filter((x) => {
      const hash = `${x.title.toLowerCase()}${x.content.toLowerCase()}${
        x.notificationTime
      }${LorenDateUtilsService.getMomentFromDate(x.notificationTime).format(
        LOREN_DATE.globalDateFormat
      )}`.toLowerCase();
      return hash.includes(this.searchString.toLowerCase());
    });

    if (this.filteredNotifications.length === 0) {
      /// getFilteredNotifications()
      this.filteredNotifications = this.allNotifications;
    }
  }

  handlePushPermissionClick() {
    // No-ops on 'granted'/'denied'; opens the priming dialog only when 'default'.
    this.messagingService.requestPermission();
  }

  markAllNotificationsAsRead() {
    this.notificationService
      .markAllNotificationsAsRead()
      .pipe(first())
      .subscribe(() => {
        this.allNotifications.forEach((x) => (x.seen = true));
        this.hasNewNotifications = false;
        this.notificationService.reloadNotificationsEvent(true);
      });
  }

  private getNotifications() {
    this.notificationService
      .getUserNotifications()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((x) => {
          x.items.forEach((v) => {
            v.path = `/${LOREN_ROUTES.NOTIFICATIONS}/${v.id}/${LOREN_ROUTES.DETAILS}`;
            v.icon = "notifications";
          });

          return x;
        })
      )
      .subscribe({
        next: (x) => {
          if (this.filteredNotifications?.[0]?.id === x.items[0]?.id) return;
          this.scrollHeight = x.items.length * this.notificationItemSize;
          /// getFilteredNotifications()
          this.filteredNotifications = x.items;
          if (this.searchString) {
            this.handleSearch();
          }
          this.allNotifications = x.items;
          // Calculate if there are new (unseen) notifications
          this.hasNewNotifications = x.items.some(notification => !notification.seen);
        },
        error: (err) => {
          this.filteredNotifications = [];
          this.allNotifications = [];
          this.snackbarService.showErrorMessage(err);
        },
      });
  }

  // private getFilteredNotifications(notifications: Array<LorenNotification>) {
  //   return notifications.map((x) => {
  //     const remainder = this.getNotificationRemainingTime(x);
  //     return { ...x, remainder };
  //   });
  // }

  // private getNotificationRetentionPeriods(companyId, userId) {
  //     this.companiesService.get(companyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
  //       this.retentionPeriodReadNotifications =
  //         res?.settings.retentionPeriodReadNotifications;
  //       this.retentionPeriodUnreadNotifications =
  //         res?.settings.retentionPeriodUnreadNotifications;
  //       this.getNotifications(userId);
  //     });
  // }

  // private getNotificationRemainingTime(n: LorenNotification) {
  //   const getDiff = (retentionPeriod) => {
  //     const now = moment();
  //     const diff = now.diff(moment(n.notificationTime), "days");
  //     return retentionPeriod ? retentionPeriod - diff : null;
  //   };
  //   return n.seen
  //     ? getDiff(this.retentionPeriodReadNotifications)
  //     : getDiff(this.retentionPeriodUnreadNotifications);
  // }

  private closeDetails() {
    this.detailsOutletService.toggleState(false);
    this.detailsOutletService.resetDetailsOutlet();
  }
}
